import React, {useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {NAVY, ORANGE} from './widgets.comp';

// Groups options into category blocks dynamically using opt.parent_label / opt.category
// Extracts numbering for each item using opt.sublabel (e.g., "CyberRiskItem1" -> 1)
const groupOptions = (options) => {
    if (!options || options.length === 0) return [];

    const groups = [];
    let currentCategory = null;
    let autoNum = 0;

    options.forEach((opt) => {
        autoNum += 1;
        const name = typeof opt === 'string' ? opt : (opt.name || opt.label || '');
        const rawCategory = typeof opt === 'object' ? (opt.parent_label || opt.category) : null;
        
        // Ignore category headers named "Headline", empty strings, or null/undefined
        const category = (rawCategory && rawCategory.trim().toLowerCase() !== 'headline') ? rawCategory.trim() : null;

        let itemNum = autoNum;
        if (typeof opt === 'object' && opt.sublabel) {
            const match = String(opt.sublabel).match(/\d+/);
            if (match) {
                itemNum = parseInt(match[0], 10);
            }
        }

        if (groups.length === 0 || category !== currentCategory) {
            groups.push({ category, items: [] });
            currentCategory = category;
        }

        groups[groups.length - 1].items.push({
            name,
            num: itemNum,
            raw: opt
        });
    });

    return groups;
};

const RiskSelector = ({ options = [], selected, onChange, disabledOptions = [] }) => {
    const activeOptions = options;

    const firstOptionName = useMemo(() => {
        if (!activeOptions || activeOptions.length === 0) return '';
        const first = activeOptions[0];
        return typeof first === 'string' ? first : (first.name || first.label || '');
    }, [activeOptions]);

    const [localSelected, setLocalSelected] = useState(firstOptionName);
    
    const activeSelected = selected !== undefined ? selected : localSelected;
    const handleSelect = (val) => {
        if (disabledOptions?.includes(val)) return;
        if (onChange) {
            onChange(val);
        } else {
            setLocalSelected(val);
        }
    };

    const groups = useMemo(() => groupOptions(activeOptions), [activeOptions]);

    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5, height: '100%',
        }}>
            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>Select a risk</Typography>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                {groups.map((grp, gi) => (
                    <Box key={grp.category || `headline-${gi}`}>
                        {grp.category && (
                            <Typography sx={{fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: ORANGE, mt: 1.5, mb: 0.5, px: 0.75}}>
                                {grp.category}
                            </Typography>
                        )}
                        {grp.items.map((item) => {
                            const o = item.name;
                            const num = String(item.num).padStart(2, '0');
                            const active = o === activeSelected;
                            const disabled = disabledOptions?.includes(o);
                            return (
                                <Box
                                    key={o}
                                    onClick={() => !disabled && handleSelect(o)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.75,
                                        borderRadius: '8px',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.5 : 1,
                                        backgroundColor: active ? 'rgba(255,105,0,0.08)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: (active && !disabled) ? 'rgba(255,105,0,0.08)' : (disabled ? 'transparent' : 'var(--brand-surface-subtle, #F9FAFB)')
                                        },
                                    }}
                                >
                                    <Typography sx={{fontSize: '13px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#9CA3AF', minWidth: 20, flexShrink: 0}}>
                                        {num}
                                    </Typography>
                                    <Typography sx={{fontSize: '13px', fontWeight: active ? 700 : 500, color: active ? NAVY : '#4B5563'}}>
                                        {o}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default RiskSelector;

